import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Receipt, Printer, Wallet } from "lucide-react";
import { toast } from "sonner";
import { FundingInvoiceSelect } from "@/components/FundingInvoiceSelect";

const CATEGORIES = ["Travel", "Fuel", "Food", "Office", "Labour", "Maintenance", "Utilities", "Material", "Other"];
const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const today = () => new Date().toISOString().slice(0, 10);
const isCredit = (e) => e.payment_status === "credit";
const paidOf = (e) => (e.paid_amount ?? (isCredit(e) ? 0 : e.amount)) || 0;
const balanceOf = (e) => Math.max(0, (e.amount || 0) - paidOf(e));

function printReport(rows, filters) {
  const total = rows.reduce((s, e) => s + (e.amount || 0), 0);
  const credit = rows.reduce((s, e) => s + balanceOf(e), 0);
  const esc = (v) => String(v ?? "").replace(/</g, "&lt;");
  const tr = rows.map((e) => `<tr><td>${esc(e.date)}</td><td>${esc(e.inward_no)}</td><td>${esc(e.invoice_no)}</td><td>${esc(e.title)}</td><td>${esc(e.category)}</td><td>${esc(e.payment_mode)}</td><td>${isCredit(e) ? "CREDIT (Due)" : "Paid"}</td><td>${esc(e.reference)}</td><td>${esc(e.remarks)}</td><td>${esc(e.notes)}</td><td>${esc(e.created_by)}</td><td class="r">${Number(e.amount || 0).toLocaleString("en-IN")}</td><td class="r">${paidOf(e).toLocaleString("en-IN")}</td><td class="r">${balanceOf(e).toLocaleString("en-IN")}</td></tr>`).join("");
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>Expense Report</title><style>
    body{font-family:Arial,sans-serif;padding:24px;color:#111}h1{margin:0 0 4px;font-size:22px}p{margin:2px 0;color:#444;font-size:12px}
    table{width:100%;border-collapse:collapse;margin-top:16px;font-size:11px}th,td{border:1px solid #999;padding:5px 6px;text-align:left}th{background:#eee}td.r,th.r{text-align:right}
    tfoot td{font-weight:bold;background:#f6f6f6}@media print{button{display:none}}</style></head><body>
    <h1>BRIJPAL SINGH EM ROF ALTANTE 108 – Expense Report</h1><p>Printed: ${new Date().toLocaleString("en-IN")}</p><p>${esc(filters)}</p>
    <table><thead><tr><th>Date</th><th>Inward No</th><th>Invoice</th><th>Title</th><th>Category</th><th>Mode</th><th>Status</th><th>Ref</th><th>Remarks</th><th>Notes</th><th>By</th><th class="r">Amount (₹)</th><th class="r">Paid (₹)</th><th class="r">Balance (₹)</th></tr></thead>
    <tbody>${tr}</tbody>
    <tfoot><tr><td colspan="11">Total Expenses</td><td class="r">${total.toLocaleString("en-IN")}</td><td class="r">${(total - credit).toLocaleString("en-IN")}</td><td class="r">${credit.toLocaleString("en-IN")}</td></tr></tfoot></table>
    <script>window.onload=function(){window.print()}</script></body></html>`);
  w.document.close();
}

export default function Expenses() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [inwards, setInwards] = useState([]);
  const [funds, setFunds] = useState(null);
  const emptyForm = { title: "", category: "Other", amount: "", date: today(), payment_mode: "cash", reference: "", notes: "", inward_id: "", remarks: "", invoice_id: "" };
  const [form, setForm] = useState(emptyForm);
  const [payFor, setPayFor] = useState(null);
  const emptyPay = { amount: "", date: today(), payment_mode: "cash", note: "", invoice_id: "" };
  const [pay, setPay] = useState(emptyPay);

  const loadFunds = () => api.get("/funds/summary").then((r) => setFunds(r.data)).catch(() => {});
  useEffect(() => {
    if (open) api.get("/movements", { params: { type: "inward" } }).then((r) => setInwards(r.data)).catch(() => {});
    if (open || payFor) loadFunds();
    // eslint-disable-next-line
  }, [open, payFor]);

  const load = () => {
    const params = {};
    if (filterCat !== "all") params.category = filterCat;
    if (start) params.start = start;
    if (end) params.end = end;
    api
      .get("/expenses", { params })
      .then((r) => setExpenses(r.data))
      .catch((e) => toast.error(errMsg(e)));
    loadFunds();
  };

  useEffect(() => { load(); }, [filterCat, start, end]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.inward_id) { toast.error("Select an Inward No first"); return; }
    if (form.payment_mode !== "credit" && !form.invoice_id) { toast.error("Select the Sales Invoice this expense is paid from"); return; }
    try {
      await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Expense added");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const openPay = (e) => { setPayFor(e); setPay({ ...emptyPay, amount: String(balanceOf(e)), invoice_id: e.invoice_id || "" }); };

  const submitPay = async (ev) => {
    ev.preventDefault();
    if (!pay.invoice_id) { toast.error("Select the Sales Invoice this payment is made from"); return; }
    try {
      const r = await api.post(`/expenses/${payFor.id}/payments`, { ...pay, amount: parseFloat(pay.amount) });
      toast.success(r.data.payment_status === "paid" ? "Fully paid" : "Payment added");
      setPayFor(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const creditTotal = expenses.reduce((s, e) => s + balanceOf(e), 0);
  const filterLabel = `Category: ${filterCat === "all" ? "All" : filterCat} · From: ${start || "—"} · To: ${end || "—"}`;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="expenses-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Daily Expenses</h1>
          <p className="text-muted-foreground mt-1">Record and track all your daily expenditures.</p>
        </div>
        <div className="flex gap-2">
        <Button variant="outline" data-testid="print-expenses-btn" onClick={() => printReport(expenses, filterLabel)} className="gap-2 border-border">
          <Printer className="h-4 w-4" /> Print Report
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-expense-btn" className="bg-sky-500 hover:bg-sky-600 text-white gap-2">
              <Plus className="h-4 w-4" /> Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Add Expense</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label>Inward No <span className="text-rose-400">*</span></Label>
                <Select value={form.inward_id} onValueChange={(v) => setForm({ ...form, inward_id: v })}>
                  <SelectTrigger data-testid="expense-inward-select"><SelectValue placeholder="Select inward entry" /></SelectTrigger>
                  <SelectContent>
                    {inwards.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground" data-testid="expense-no-inward-msg">No inward entries. Add an inward entry first.</div>
                    ) : inwards.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.inward_no || m.id.slice(0, 8)} · {m.item_name} · {m.date}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input data-testid="expense-title-input" required value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Diesel for generator" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input data-testid="expense-amount-input" type="number" step="0.01" min="0" required
                    value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input data-testid="expense-date-input" type="date" required value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Payment Mode</Label>
                  <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                    <SelectTrigger data-testid="expense-payment-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank">Bank Transfer</SelectItem>
                      <SelectItem value="credit">Credit (Pay Later)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.payment_mode !== "credit" && (
                <FundingInvoiceSelect funds={funds} value={form.invoice_id} onChange={(v) => setForm({ ...form, invoice_id: v })} testid="expense-invoice-select" />
              )}
              <div className="space-y-2">
                <Label>Reference / Bill No.</Label>
                <Input data-testid="expense-reference-input" value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Optional" />
              </div>
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input data-testid="expense-remarks-input" value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Optional" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea data-testid="expense-notes-input" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="expense-submit-btn" className="bg-sky-500 hover:bg-sky-600 text-white">Save Expense</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters + total */}
      {funds && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="funds-cards">
          <Card className="bg-card border-border"><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground font-mono-sku">Received From Invoices</p>
            <p className="text-2xl font-extrabold text-sky-400" data-testid="funds-received">{fmt(funds.received)}</p>
          </CardContent></Card>
          <Card className="bg-card border-border"><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground font-mono-sku">Spent (Expenses + Vendors)</p>
            <p className="text-2xl font-extrabold text-rose-400" data-testid="funds-spent">{fmt(funds.used_expenses + funds.used_vendor)}</p>
            <p className="text-xs text-muted-foreground">Expenses {fmt(funds.used_expenses)} · Vendors {fmt(funds.used_vendor)}</p>
          </CardContent></Card>
          <Card className="bg-emerald-500/10 border-emerald-500/20"><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground font-mono-sku">Total Balance Available</p>
            <p className="text-2xl font-extrabold text-emerald-400" data-testid="funds-available">{fmt(funds.available)}</p>
          </CardContent></Card>
          <Card className="bg-sky-500/10 border-sky-500/20"><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground font-mono-sku">To Be Credited (Invoice Due)</p>
            <p className="text-2xl font-extrabold text-sky-300" data-testid="funds-receivable">{fmt(funds.receivable)}</p>
          </CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs">Category</Label>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-40" data-testid="expense-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" className="w-40" value={start} onChange={(e) => setStart(e.target.value)} data-testid="expense-filter-start" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="w-40" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="expense-filter-end" />
        </div>
        <Card className="bg-amber-500/10 border-amber-500/20 ml-auto">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground font-mono-sku">Credit Balance (Due)</p>
            <p className="text-2xl font-extrabold text-amber-400" data-testid="expense-credit-balance">{fmt(creditTotal)}</p>
          </CardContent>
        </Card>
        <Card className="bg-rose-500/10 border-rose-500/20">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground font-mono-sku">Total Shown</p>
            <p className="text-2xl font-extrabold text-rose-400" data-testid="expense-total">{fmt(total)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Inward No</th>
                <th className="px-4 py-3 font-medium">Invoice</th>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Mode</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Ref</th>
                <th className="px-4 py-3 font-medium">Remarks</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium text-right">Paid</th>
                <th className="px-4 py-3 font-medium text-right">Balance</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="expense-table-body">
              {expenses.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-12 text-center text-muted-foreground">
                  <Receipt className="h-10 w-10 mx-auto mb-2 opacity-40" /> No expenses recorded.
                </td></tr>
              ) : expenses.map((e) => (
                <tr key={e.id} className="border-b border-border/50 hover:bg-white/5">
                  <td className="px-4 py-3 font-mono-sku text-xs">{e.date}</td>
                  <td className="px-4 py-3 font-mono-sku text-xs text-sky-300" data-testid={`expense-inward-no-${e.id}`}>{e.inward_no || "—"}</td>
                  <td className="px-4 py-3 font-mono-sku text-xs text-violet-300" data-testid={`expense-invoice-no-${e.id}`}>{e.invoice_no || "—"}</td>
                  <td className="px-4 py-3 font-medium">{e.title}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-sky-300 border-sky-500/30">{e.category}</Badge></td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{e.payment_mode}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" data-testid={`expense-status-${e.id}`}
                      className={isCredit(e) ? "text-amber-400 border-amber-500/40 bg-amber-500/10" : "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"}>
                      {isCredit(e) ? (paidOf(e) > 0 ? "Partial" : "Credit") : "Paid"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{e.reference || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate" data-testid={`expense-remarks-${e.id}`}>{e.remarks || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-sku font-semibold">{fmt(e.amount)}</td>
                  <td className="px-4 py-3 text-right font-mono-sku text-emerald-400" data-testid={`expense-paid-${e.id}`}>{fmt(paidOf(e))}</td>
                  <td className="px-4 py-3 text-right font-mono-sku text-amber-400" data-testid={`expense-balance-${e.id}`}>{balanceOf(e) > 0 ? fmt(balanceOf(e)) : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {balanceOf(e) > 0 && (
                      <Button size="sm" variant="ghost" data-testid={`expense-pay-${e.id}`}
                        onClick={() => openPay(e)} className="h-8 gap-1 text-emerald-400 hover:bg-emerald-500/10">
                        <Wallet className="h-4 w-4" /> Pay
                      </Button>
                    )}
                    {user?.role === "admin" && (
                    <Button size="icon" variant="ghost" data-testid={`expense-delete-${e.id}`}
                      onClick={() => del(e.id)} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Add Payment</DialogTitle>
          </DialogHeader>
          {payFor && (
            <form onSubmit={submitPay} className="space-y-4">
              <div className="rounded-lg bg-white/5 p-3 text-sm flex justify-between">
                <span className="font-medium">{payFor.title}</span>
                <span className="text-amber-400 font-mono-sku" data-testid="pay-dialog-balance">Due {fmt(balanceOf(payFor))}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input data-testid="payment-amount-input" type="number" step="0.01" min="0.01" max={balanceOf(payFor)} required
                    value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input data-testid="payment-date-input" type="date" required value={pay.date}
                    onChange={(e) => setPay({ ...pay, date: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Mode</Label>
                  <Select value={pay.payment_mode} onValueChange={(v) => setPay({ ...pay, payment_mode: v })}>
                    <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Note</Label>
                  <Input data-testid="payment-note-input" value={pay.note}
                    onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="Optional" />
                </div>
              </div>
              <FundingInvoiceSelect funds={funds} value={pay.invoice_id} onChange={(v) => setPay({ ...pay, invoice_id: v })} testid="payment-invoice-select" />
              {payFor.payments?.length > 0 && (
                <div className="space-y-1" data-testid="payment-history">
                  <p className="text-xs uppercase text-muted-foreground font-mono-sku">Payment History</p>
                  {payFor.payments.map((p) => (
                    <div key={p.id} className="flex justify-between text-xs text-muted-foreground">
                      <span>{p.date} · {p.payment_mode}{p.note ? ` · ${p.note}` : ""} · {p.by}</span>
                      <span className="text-emerald-400 font-mono-sku">{fmt(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button type="submit" data-testid="payment-submit-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white">Add Payment</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
